import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import { FilterVariables } from 'modules/core/components/MultiFilters/hooks/types'
import type { Employee as CurrentEmployee } from 'modules/core/hooks/useCurrentSession'

import type Employee from 'modules/employees/store/models/Employee'

import defaultEvaluators from './defaultEvaluators'
import type { Value } from './defaultEvaluators'
import { logicOperators } from './logicOperators'

export type CustomEvaluatorFn = (
  employee: Employee,
  filterValue: Value,
  currentEmployee: CurrentEmployee,
  allMyDescendants: string[],
  variables: FilterVariables
) => boolean

export interface ExtraHooks {
  beforeFilter: (filtersToExecute: MultiFilterForm[]) => Promise<void>
  skipModelFilter: <T>(filters: MultiFilterForm[], model: T) => boolean
  getDefaultFilters: (filters: MultiFilterForm[]) => MultiFilterForm[] | null
  customEvaluators: Record<string, Record<string, CustomEvaluatorFn>>
}

const applyFilters = async <T>(
  models: T[],
  filters: MultiFilterForm[],
  logicOperator: LogicOperatorForm,
  getFilterValue: (model: T, fieldName: any) => any,
  setFilteredModels: (models: T[]) => void,
  { beforeFilter, skipModelFilter, getDefaultFilters, customEvaluators }: ExtraHooks,
  currentEmployee: CurrentEmployee,
  allMyDescendants: string[],
  variables: FilterVariables = {}
) => {
  const filterModels = (models: T[], filters: MultiFilterForm[], logicOperator: string): T[] => {
    return models.filter((model) => {
      if (skipModelFilter(filters, model)) return true

      const filteredResults = new Set(
        filters.map((filter) => {
          // NOTE: these two strings could be properly typed using union strings
          const fieldName: string = filter.get('field').value
          const condition: string = filter.get('condition').value

          const customEvaluator = customEvaluators[fieldName]?.[condition]
          const evaluator = customEvaluator || defaultEvaluators[condition]

          // ignore filter when evaluator was not found (for legacy filters in the user's local storage)
          if (!evaluator) return true

          return evaluator(
            getFilterValue(model, fieldName),
            filter.get('value')?.value,
            currentEmployee,
            allMyDescendants,
            variables
          )
        })
      )

      if (filters.length === 0) return true

      switch (logicOperator) {
        case logicOperators.and:
          return !filteredResults.has(false)

        case logicOperators.or:
          return filteredResults.has(true)
      }
    })
  }

  const filtersToExecute = filters.filter((filter) => {
    const isCustomAction = filter.get('custom_action').value === true
    const hasCondition = !!filter.get('condition').value

    return !isCustomAction && hasCondition
  })
  await beforeFilter(filtersToExecute)

  const defaultFilters = getDefaultFilters(filtersToExecute)

  const defaultFilteredModels = defaultFilters
    ? filterModels(models, defaultFilters, logicOperators.and)
    : models

  const value = filterModels(
    defaultFilteredModels,
    filtersToExecute,
    logicOperator.get('logic_operator').value
  )
  setFilteredModels(value)

  return value
}

export default applyFilters
